import React, { useEffect, useState } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
	Box,
	CircularProgress,
	Button,
	Chip,
	MenuItem,
	Select,
	SelectChangeEvent,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	TextField,
	TablePagination,
	TableSortLabel,
	InputAdornment,
	FormControl,
	InputLabel,
	Grid,
	Stack,
	useMediaQuery,
	Theme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Booking } from '../../../types/Booking';
import { getAllBookings, updateBooking } from '../../../services/api/Booking';

const STATUS_OPTIONS = {
	NEW: 'New',
	PENDING: 'Pending',
	CONVERTED: 'Converted',
	REJECTED: 'Rejected',
};

type Order = 'asc' | 'desc';

interface HeadCell {
	id: keyof Booking;
	label: string;
}

const headCells: HeadCell[] = [
	{ id: 'bookingID', label: 'Booking ID' },
	{ id: 'name', label: 'Name' },
	{ id: 'eventType', label: 'Event Type' },
	{ id: 'eventDate', label: 'Event Date' },
	{ id: 'noOfPeoples', label: 'People' },
	{ id: 'mobile', label: 'Mobile' },
	{ id: 'status', label: 'Status' },
	// { id: 'followUp', label: 'Follow Up' },
];

const BookingList: React.FC = () => {
	const [bookings, setBookings] = useState<Booking[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [editingFollowUp, setEditingFollowUp] = useState('');

	// Pagination state
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(5);

	// Sorting state
	const [order, setOrder] = useState<Order>('desc');
	const [orderBy, setOrderBy] = useState<keyof Booking>('updatedAt');

	// Filtering state
	const [searchTerm, setSearchTerm] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('all');

	// Responsive breakpoints
	const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between('sm', 'md'));
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	useEffect(() => {
		const fetchBookings = async () => {
			try {
				const response = await getAllBookings();
				if (response.success) {
					const fetchedBookings = response?.data?.bookings || [];
					// Sort by updatedAt descending by default
					const sortedBookings = [...fetchedBookings].sort(
						(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
					);
					setBookings(sortedBookings);
				} else {
					setError('Failed to fetch bookings');
				}
			} catch (err) {
				setError('An error occurred while fetching bookings');
			} finally {
				setLoading(false);
			}
		};

		fetchBookings();
	}, []);

	const getStatusColor = (status: string) => {
		switch (status) {
			case 'New':
				return 'info';
			case 'Pending':
				return 'warning';
			case 'Converted':
				return 'success';
			case 'Rejected':
				return 'error';
			default:
				return 'default';
		}
	};

	const handleStatusChange = async (event: SelectChangeEvent, bookingId: string) => {
		const newStatus = event.target.value as string;

		try {
			const response = await updateBooking(bookingId, {
				status: newStatus,
				followUp: bookings.find((b) => b._id === bookingId)?.followUp || '',
			});
			if (response.success) {
				setBookings((prevBookings) =>
					prevBookings.map((booking) =>
						booking._id === bookingId
							? { ...booking, status: newStatus, updatedAt: new Date().toISOString() }
							: booking
					)
				);
			}
		} catch (err) {
			console.error('Failed to update booking status:', err);
		}
	};

	const handleViewBooking = (booking: Booking) => {
		setSelectedBooking(booking);
		setEditingFollowUp(booking.followUp || '');
		setOpenDialog(true);
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
	};

	const handleSaveChanges = async () => {
		if (!selectedBooking) return;

		try {
			const response = await updateBooking(selectedBooking._id, {
				status: selectedBooking.status,
				followUp: editingFollowUp,
			});
			if (response.success) {
				if (response?.success && response?.data) {
					const updatedBooking = response.data as Booking;
					setBookings((prevBookings) =>
						prevBookings.map((booking) =>
							booking._id === updatedBooking._id ? updatedBooking : booking
						)
					);
				}
				setSelectedBooking({
					...selectedBooking,
					followUp: editingFollowUp,
				});
				handleCloseDialog();
			}
		} catch (err) {
			console.error('Failed to update booking:', err);
		}
	};

	// Sorting functions
	const handleRequestSort = (property: keyof Booking) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const stableSort = (array: Booking[]) => {
		const stabilizedThis = array.map((el, index) => [el, index] as [Booking, number]);

		stabilizedThis.sort((a, b) => {
			const orderFactor = order === 'desc' ? -1 : 1;

			// Handle different types of properties
			if (orderBy === 'eventDate' || orderBy === 'updatedAt') {
				const dateA = new Date(a[0][orderBy]).getTime();
				const dateB = new Date(b[0][orderBy]).getTime();
				return (dateA - dateB) * orderFactor;
			} else if (orderBy === 'noOfPeoples') {
				return (a[0][orderBy] - b[0][orderBy]) * orderFactor;
			} else {
				// For string properties
				const valueA = String(a[0][orderBy]).toLowerCase();
				const valueB = String(b[0][orderBy]).toLowerCase();
				if (valueA < valueB) return -1 * orderFactor;
				if (valueA > valueB) return 1 * orderFactor;
				return 0;
			}
		});

		return stabilizedThis.map((el) => el[0]);
	};

	// Filtering functions
	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const valuesad = event.target.value.toLowerCase();
		setSearchTerm(valuesad);
		setPage(0); // Reset to first page when search changes
	};

	const handleStatusFilterChange = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value as string);
		setPage(0); // Reset to first page when status filter changes
	};

	const applyFilters = (data: Booking[]) =>
		data.filter((booking) => {
			// Apply status filter
			if (statusFilter !== 'all' && booking.status !== statusFilter) {
				return false;
			}

			// Skip search filter if empty
			if (!searchTerm) return true;

			// Check all searchable fields
			const searchableFields = [
				booking.bookingID,
				booking.name,
				typeof booking.eventType === 'object'
					? booking.eventType.eventType
					: booking.eventType,
				booking.status,
				booking.followUp,
				booking.additionalInfo,
				booking.mobile,
			];

			return searchableFields.some(
				(field) => field && field.toString().toLowerCase().includes(searchTerm)
			);
		});

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	// Get filtered and sorted data
	const filteredBookings = applyFilters(bookings);
	const sortedBookings = stableSort(filteredBookings);
	const paginatedBookings = sortedBookings.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage
	);

	// Empty state
	const emptyRows =
		page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredBookings.length) : 0;

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
				<Typography color="error">{error}</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: isMobile ? 1 : 3 }}>
			{/* Unified Search and Filter */}
			<Box
				sx={{
					display: 'flex',
					gap: 2,
					mb: 3,
					flexDirection: isMobile ? 'column' : 'row',
					alignItems: isMobile ? 'stretch' : 'center',
				}}
			>
				<TextField
					label="Search bookings"
					variant="outlined"
					size="small"
					value={searchTerm}
					onChange={handleSearchChange}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
					fullWidth={isMobile}
					placeholder={
						isMobile
							? 'Search...'
							: 'Search by ID, name, event type, status, or notes...'
					}
				/>

				<FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
					<InputLabel>Status</InputLabel>
					<Select
						value={statusFilter}
						onChange={handleStatusFilterChange}
						label="Status"
						fullWidth={isMobile}
					>
						<MenuItem value="all">All Statuses</MenuItem>
						{Object.values(STATUS_OPTIONS).map((status) => (
							<MenuItem key={status} value={status}>
								{status}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Box>

			<TableContainer
				component={Paper}
				sx={{ maxHeight: isMobile ? 'calc(100vh - 200px)' : 'none' }}
			>
				<Table stickyHeader size={isMobile ? 'small' : 'medium'}>
					<TableHead>
						<TableRow>
							{headCells.map((headCell) => (
								<TableCell
									key={headCell.id}
									sortDirection={orderBy === headCell.id ? order : false}
									sx={{
										display:
											isMobile &&
											(headCell.id === 'bookingID' ||
												headCell.id === 'noOfPeoples')
												? 'none'
												: 'table-cell',
									}}
								>
									<TableSortLabel
										active={orderBy === headCell.id}
										direction={orderBy === headCell.id ? order : 'asc'}
										onClick={() => handleRequestSort(headCell.id)}
									>
										{isTablet && headCell.label.length > 8
											? `${headCell.label.substring(0, 8)}...`
											: headCell.label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{paginatedBookings.map((booking) => (
							<TableRow key={booking._id} hover>
								<TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
									{booking.bookingID}
								</TableCell>
								<TableCell>
									{isMobile
										? `${booking.name.split(' ')[0]}${booking.name.split(' ').length > 1 ? '...' : ''}`
										: booking.name}
								</TableCell>
								<TableCell>
									{typeof booking.eventType === 'object'
										? isMobile
											? `${booking.eventType.eventType.substring(0, 5)}...`
											: booking.eventType.eventType
										: isMobile
											? `${booking.eventType.substring(0, 5)}...`
											: booking.eventType}
								</TableCell>
								<TableCell>
									{isMobile
										? new Date(booking.eventDate).toLocaleDateString('en-US', {
												month: 'short',
												day: 'numeric',
											})
										: new Date(booking.eventDate).toLocaleDateString()}
								</TableCell>
								<TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
									{booking.noOfPeoples}
								</TableCell>
								<TableCell>
									{isMobile ? `...${booking.mobile.slice(-4)}` : booking.mobile}
								</TableCell>
								<TableCell>
									<Select
										value={booking.status}
										onChange={(e) => handleStatusChange(e, booking._id)}
										size="small"
										sx={{
											minWidth: isMobile ? 80 : 120,
											height: 32,
											border: 'none',
											'& .MuiOutlinedInput-notchedOutline': {
												border: 'none',
											},
											'&:hover .MuiOutlinedInput-notchedOutline': {
												border: 'none',
											},
											'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
												border: 'none',
											},
											'& .MuiSelect-select': {
												paddingTop: '6px',
												paddingBottom: '6px',
												fontSize: isMobile ? '0.75rem' : '0.875rem',
											},
										}}
									>
										{Object.values(STATUS_OPTIONS).map((status) => (
											<MenuItem key={status} value={status}>
												<Chip
													label={
														isMobile ? status.substring(0, 3) : status
													}
													color={getStatusColor(status)}
													size="small"
												/>
											</MenuItem>
										))}
									</Select>
								</TableCell>
								{/* <TableCell>
									{booking.followUp
										? isMobile
											? `${booking.followUp.substring(0, 10)}...`
											: booking.followUp.length > 30
												? `${booking.followUp.substring(0, 30)}...`
												: booking.followUp
										: 'N/A'}
								</TableCell> */}
								<TableCell>
									<Button
										variant="outlined"
										size="small"
										onClick={() => handleViewBooking(booking)}
										sx={{
											minWidth: isMobile ? 60 : 80,
											'&:hover': {
												backgroundColor: 'primary.main',
												color: 'white',
											},
										}}
									>
										{isMobile ? 'View' : 'View'}
									</Button>
								</TableCell>
							</TableRow>
						))}
						{emptyRows > 0 && (
							<TableRow style={{ height: 53 * emptyRows }}>
								<TableCell colSpan={headCells.length + 1} />
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Pagination */}
			<TablePagination
				rowsPerPageOptions={[5, 10, 25]}
				component="div"
				count={filteredBookings.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				sx={{
					'& .MuiTablePagination-toolbar': {
						flexWrap: 'wrap',
						justifyContent: 'center',
					},
					'& .MuiTablePagination-actions': {
						marginTop: isMobile ? 1 : 0,
					},
				}}
			/>

			{/* Booking Details Dialog */}
			<Dialog
				open={openDialog}
				onClose={handleCloseDialog}
				maxWidth="md"
				fullWidth
				fullScreen={isMobile}
				PaperProps={{
					sx: {
						borderRadius: isMobile ? 0 : 2,
						boxShadow: 24,
					},
				}}
			>
				<DialogTitle
					sx={{
						// backgroundColor: 'primary.main',
						color: 'black',
						padding: 2,
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h6" fontWeight="bold">
						Booking Details
					</Typography>
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						sx={{
							color: 'white',
							'&:hover': {
								backgroundColor: 'primary.dark',
							},
						}}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ padding: isMobile ? 1 : 3 }}>
					{selectedBooking && (
						<Box sx={{ mt: 1 }}>
							<Typography
								variant={isMobile ? 'h6' : 'h5'}
								gutterBottom
								fontWeight="bold"
								color="primary.main"
							>
								{selectedBooking.name}
							</Typography>

							<Grid
								container
								spacing={isMobile ? 1 : 3}
								sx={{ mb: isMobile ? 1 : 3 }}
							>
								<Grid item xs={12} md={6}>
									<Paper
										elevation={0}
										sx={{
											p: isMobile ? 1 : 2,
											borderLeft: '4px solid',
											borderColor: 'primary.main',
											backgroundColor: 'grey.50',
										}}
									>
										<Typography
											variant="subtitle1"
											fontWeight="bold"
											gutterBottom
											color="text.secondary"
										>
											Booking Information
										</Typography>

										<Stack spacing={1}>
											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Booking ID
												</Typography>
												<Typography>{selectedBooking.bookingID}</Typography>
											</Box>

											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Event Type
												</Typography>
												<Typography>
													{typeof selectedBooking.eventType === 'object'
														? selectedBooking.eventType.eventType
														: selectedBooking.eventType}
												</Typography>
											</Box>

											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Event Date
												</Typography>
												<Typography>
													{new Date(
														selectedBooking.eventDate
													).toLocaleDateString('en-US', {
														weekday: isMobile ? undefined : 'long',
														year: 'numeric',
														month: 'long',
														day: 'numeric',
													})}
												</Typography>
											</Box>

											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Number of People
												</Typography>
												<Typography>
													{selectedBooking.noOfPeoples}
												</Typography>
											</Box>

											<Box>
												<Typography
													variant="caption"
													color="text.secondary"
												>
													Last Updated
												</Typography>
												<Typography>
													{new Date(
														selectedBooking.updatedAt
													).toLocaleString()}
												</Typography>
											</Box>
										</Stack>
									</Paper>
								</Grid>

								<Grid item xs={12} md={6}>
									<Paper
										elevation={0}
										sx={{
											p: isMobile ? 1 : 2,
											borderLeft: '4px solid',
											borderColor: 'secondary.main',
											backgroundColor: 'grey.50',
										}}
									>
										<Typography
											variant="subtitle1"
											fontWeight="bold"
											gutterBottom
											color="text.secondary"
										>
											Status Management
										</Typography>

										<Select
											value={selectedBooking.status}
											onChange={(e) => {
												setSelectedBooking({
													...selectedBooking,
													status: e.target.value as string,
												});
											}}
											fullWidth
											sx={{ mb: 2 }}
											variant="outlined"
										>
											{Object.values(STATUS_OPTIONS).map((status) => (
												<MenuItem key={status} value={status}>
													<Chip
														label={status}
														color={getStatusColor(status)}
														size="small"
														sx={{ width: '100%' }}
													/>
												</MenuItem>
											))}
										</Select>

										<Box sx={{ mt: 2 }}>
											<Typography
												variant="subtitle1"
												fontWeight="bold"
												gutterBottom
												color="text.secondary"
											>
												Follow Up Notes
											</Typography>
											<TextField
												multiline
												rows={isMobile ? 3 : 4}
												fullWidth
												value={editingFollowUp}
												onChange={(e) => setEditingFollowUp(e.target.value)}
												variant="outlined"
												placeholder="Add follow up notes..."
											/>
										</Box>

										{selectedBooking?.additionalInfo && (
											<Box sx={{ mt: 2 }}>
												<Typography
													variant="subtitle1"
													fontWeight="bold"
													gutterBottom
													color="text.secondary"
												>
													Additional Information
												</Typography>
												<Paper variant="outlined" sx={{ p: 2 }}>
													<Typography sx={{ whiteSpace: 'pre-line' }}>
														{selectedBooking.additionalInfo}
													</Typography>
												</Paper>
											</Box>
										)}
									</Paper>
								</Grid>
							</Grid>
						</Box>
					)}
				</DialogContent>

				<DialogActions sx={{ padding: 2 }}>
					<Button
						onClick={handleCloseDialog}
						variant="outlined"
						sx={{
							px: 3,
							'&:hover': {
								backgroundColor: 'grey.100',
							},
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSaveChanges}
						sx={{
							px: 3,
							'&:hover': {
								backgroundColor: 'primary.dark',
							},
						}}
					>
						Save Changes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default BookingList;
